import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AutoComplete, Input, Spin} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {AutocompleteSuggestion, SearchService} from '../../service/search.service';
import {debounce} from 'lodash';
import './SearchBar.css';

const { Search } = Input;

interface SearchBarProps {
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  onSearch?: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search news...',
  size = 'middle',
  style,
  onSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounced autocomplete function
  const fetchSuggestions = useCallback((query: string) => {
    const debouncedFetch = debounce(async () => {
      if (!query || query.length < 2) {
        setSuggestions([]);
        setOpen(false);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setLoading(true);
      try {
        const response = await SearchService.autocomplete(query, 8);
        setSuggestions(response.suggestions);
        setOpen(response.suggestions.length > 0);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          // Silently handle autocomplete errors
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    debouncedFetch();
  }, []);

  useEffect(() => {
    fetchSuggestions(searchQuery);

    // Cleanup
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchQuery, fetchSuggestions]);

  const handleSearch = (value: string) => {
    if (!value.trim()) return;

    setOpen(false);
    if (onSearch) {
      onSearch(value);
    } else {
      navigate(`/search?q=${encodeURIComponent(value)}`);
    }
  };

  const handleSelect = (value: string, option: any) => {
    const suggestion = suggestions.find((s) => s.text === value);
    if (suggestion && suggestion.cluster_id) {
      navigate(`/cluster/${suggestion.cluster_id}`);
    } else {
      handleSearch(value);
    }
  };

  const options = suggestions.map((suggestion) => ({
    value: suggestion.text,
    label: (
      <div className="suggestion-item">
        <SearchOutlined style={{ marginRight: 8, color: '#999' }} />
        <span>{suggestion.text}</span>
      </div>
    ),
  }));

  return (
    <div className="search-bar-container" style={{ display: 'flex', alignItems: 'center', ...style }}>
      <AutoComplete
        options={options}
        value={searchQuery}
        onChange={setSearchQuery}
        onSelect={handleSelect}
        open={open}
        onOpenChange={setOpen}
        style={{ width: '100%' }}
        notFoundContent={loading ? <Spin size="small" /> : null}
      >
        <Search
          placeholder={placeholder}
          allowClear
          enterButton={<SearchOutlined />}
          size={size}
          onSearch={handleSearch}
          loading={loading}
        />
      </AutoComplete>
    </div>
  );
};

export default SearchBar;
